import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useBrand } from "@/contexts/BrandContext";
import { counselorBrandFor } from "@/lib/counselor-brand";

export type AssistantProfile = "campaign" | "platform";

export interface VisibleAssistant {
  id: string;
  name: string;
  language: string;
  scope: string;
  organization_id: string | null;
}

export interface ChatMsg {
  id?: string;
  role: "user" | "assistant";
  content: string;
  sources?: Array<{ excerpt: string }>;
  actions?: Array<{ type: string; path?: string; label?: string; source_id?: string }>;
}

export interface ChatThread {
  id: string;
  title: string;
  scope: AssistantProfile;
  assistant_id: string | null;
  organization_id: string | null;
  updated_at: string;
  created_at: string;
}

function titleFromPrompt(text: string): string {
  const clean = text.replace(/\s+/g, " ").trim();
  if (clean.length <= 48) return clean || "Nueva conversación";
  return `${clean.slice(0, 48).trim()}…`;
}

export interface StudySection {
  label: string;
}

interface UseAssistantChatOptions {
  /** Lock the chat to a single profile — hides the mode selector. */
  forceProfile?: AssistantProfile;
}

export function useAssistantChat(options?: UseAssistantChatOptions) {
  const { activeWorkspaceId, agent } = useAuth();
  const brand = useBrand();
  const counselorBrand = counselorBrandFor(brand.id);
  const userId = agent?.user_id ?? null;
  const forceProfile = options?.forceProfile;

  const [profile, setProfileState] = useState<AssistantProfile>(forceProfile ?? "campaign");
  const [assistants, setAssistants] = useState<VisibleAssistant[]>([]);
  const [campaignId, setCampaignId] = useState<string | null>(null);
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [sending, setSending] = useState(false);
  const [studySections, setStudySections] = useState<StudySection[]>([]);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const threadIdRef = useRef<string | null>(null);

  useEffect(() => {
    threadIdRef.current = threadId;
  }, [threadId]);

  const campaignAssistants = assistants.filter((a) => a.scope === "campaign");
  const platformAssistant = assistants.find((a) => a.scope === "platform");
  const activeCampaign = campaignAssistants.find((a) => a.id === campaignId) ?? null;

  const loadAssistants = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc("list_my_knowledge_assistants");
      if (error) throw error;
      const list = (data as VisibleAssistant[]) ?? [];
      setAssistants(list);
      const forActive = list.find(
        (a) => a.scope === "campaign" && a.organization_id === activeWorkspaceId,
      );
      const anyCampaign = list.find((a) => a.scope === "campaign");
      setCampaignId(forActive?.id ?? anyCampaign?.id ?? null);
      if (!forceProfile && !forActive && !anyCampaign) setProfileState("platform");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudieron cargar asistentes");
    }
  }, [activeWorkspaceId]);

  const loadThreads = useCallback(async () => {
    if (!userId) {
      setThreads([]);
      return;
    }
    const { data, error } = await supabase
      .from("knowledge_assistant_threads")
      .select("id, title, scope, assistant_id, organization_id, updated_at, created_at")
      .eq("user_id", userId)
      .is("hidden_by_author_at", null)
      .order("updated_at", { ascending: false })
      .limit(80);
    if (error) {
      console.error(error);
      return;
    }
    setThreads((data as ChatThread[]) ?? []);
  }, [userId]);

  const loadStudySections = useCallback(async () => {
    if (!campaignId) {
      setStudySections([]);
      return;
    }
    try {
      // Find the KB id for the active campaign assistant
      const { data: assistant } = await supabase
        .from("knowledge_assistants")
        .select("kb_id")
        .eq("id", campaignId)
        .single();
      if (!assistant?.kb_id) {
        setStudySections([]);
        return;
      }
      const { data: rows } = await supabase
        .from("kb_sources")
        .select("section_label")
        .eq("kb_id", assistant.kb_id)
        .not("section_label", "is", null)
        .order("section_label");
      // Deduplicate section labels
      const seen = new Set<string>();
      const sections: StudySection[] = [];
      for (const r of (rows ?? []) as { section_label: string }[]) {
        const label = r.section_label.trim();
        if (label && !seen.has(label)) {
          seen.add(label);
          sections.push({ label });
        }
      }
      setStudySections(sections);
    } catch {
      setStudySections([]);
    }
  }, [campaignId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await Promise.all([loadAssistants(), loadThreads()]);
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [loadAssistants, loadThreads]);

  // Reload sections when the active campaign changes
  useEffect(() => {
    void loadStudySections();
  }, [loadStudySections]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  const openThread = useCallback(async (id: string) => {
    setLoadingThread(true);
    setThreadId(id);
    try {
      const [{ data: thread }, { data: rows, error }] = await Promise.all([
        supabase
          .from("knowledge_assistant_threads")
          .select("id, title, scope, assistant_id, organization_id")
          .eq("id", id)
          .single(),
        supabase
          .from("knowledge_assistant_messages")
          .select("id, role, content, sources, actions")
          .eq("thread_id", id)
          .order("created_at", { ascending: true }),
      ]);
      if (error) throw error;
      if (thread) {
        setProfileState(thread.scope as AssistantProfile);
        if (thread.scope === "campaign" && thread.assistant_id) {
          setCampaignId(thread.assistant_id);
        }
      }
      setMessages(
        ((rows as ChatMsg[]) ?? []).map((m) => ({
          id: m.id,
          role: m.role,
          content: m.content,
          sources: (m.sources as ChatMsg["sources"]) ?? [],
          actions: (m.actions as ChatMsg["actions"]) ?? [],
        })),
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo abrir la conversación");
    } finally {
      setLoadingThread(false);
    }
  }, []);

  const newChat = useCallback(() => {
    setThreadId(null);
    setMessages([]);
    setInput("");
    setActiveSection(null);
  }, []);

  const setProfile = useCallback(
    (next: AssistantProfile) => {
      if (forceProfile) return; // locked to a single profile
      setProfileState(next);
      // Switching mode starts a fresh draft (ChatGPT-like).
      if (threadIdRef.current || messages.length > 0) {
        setThreadId(null);
        setMessages([]);
      }
    },
    [messages.length, forceProfile],
  );

  const deleteThread = useCallback(
    async (id: string) => {
      const { error } = await supabase
        .from("knowledge_assistant_threads")
        .update({ hidden_by_author_at: new Date().toISOString() })
        .eq("id", id);
      if (error) {
        toast.error(error.message);
        return;
      }
      setThreads((prev) => prev.filter((t) => t.id !== id));
      if (threadIdRef.current === id) newChat();
    },
    [newChat],
  );

  const ensureThread = useCallback(
    async (firstMessage: string): Promise<string> => {
      if (threadIdRef.current) return threadIdRef.current;
      if (!userId) throw new Error("Sesión no disponible");

      const assistantId =
        profile === "campaign" ? campaignId : platformAssistant?.id ?? null;
      const orgId =
        profile === "campaign"
          ? activeCampaign?.organization_id ?? activeWorkspaceId
          : activeWorkspaceId;

      const { data, error } = await supabase
        .from("knowledge_assistant_threads")
        .insert({
          user_id: userId,
          organization_id: orgId,
          assistant_id: assistantId,
          scope: profile,
          title: titleFromPrompt(firstMessage),
        })
        .select("id, title, scope, assistant_id, organization_id, updated_at, created_at")
        .single();
      if (error || !data) throw error ?? new Error("No se pudo crear la conversación");

      const created = data as ChatThread;
      setThreadId(created.id);
      threadIdRef.current = created.id;
      setThreads((prev) => [created, ...prev.filter((t) => t.id !== created.id)]);
      return created.id;
    },
    [
      userId,
      profile,
      campaignId,
      platformAssistant?.id,
      activeCampaign?.organization_id,
      activeWorkspaceId,
    ],
  );

  const send = async (opts?: { text?: string; studySourceId?: string }) => {
    const text = (opts?.text ?? input).trim();
    if (!text || sending) return;

    if (profile === "campaign" && !campaignId) {
      toast.error("No hay asistente de campaña para tus workspaces");
      return;
    }
    if (profile === "platform" && !platformAssistant) {
      toast.error("Consejero Flow no disponible aún");
      return;
    }

    const next: ChatMsg[] = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setSending(true);

    try {
      const tid = await ensureThread(text);

      await supabase.from("knowledge_assistant_messages").insert({
        thread_id: tid,
        role: "user",
        content: text,
      });

      const body =
        profile === "campaign"
          ? {
              assistant_id: campaignId,
              organization_id: activeCampaign?.organization_id ?? activeWorkspaceId,
              messages: next.map((m) => ({ role: m.role, content: m.content })),
              ...(opts?.studySourceId ? { study_source_id: opts.studySourceId } : {}),
              ...(activeSection ? { section_label: activeSection } : {}),
            }
          : {
              scope: "platform",
              assistant_id: platformAssistant?.id,
              organization_id: activeWorkspaceId,
              brand: counselorBrand,
              messages: next.map((m) => ({ role: m.role, content: m.content })),
            };

      const { data, error } = await supabase.functions.invoke("assistant-chat", { body });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      const reply: ChatMsg = {
        role: "assistant",
        content: data.reply ?? "",
        sources: data.sources ?? [],
        actions: data.actions ?? [],
      };
      setMessages((prev) => [...prev, reply]);

      await supabase.from("knowledge_assistant_messages").insert({
        thread_id: tid,
        role: "assistant",
        content: reply.content,
        sources: reply.sources ?? [],
        actions: reply.actions ?? [],
      });

      const now = new Date().toISOString();
      await supabase
        .from("knowledge_assistant_threads")
        .update({ updated_at: now })
        .eq("id", tid);

      setThreads((prev) => {
        const rest = prev.filter((t) => t.id !== tid);
        const current = prev.find((t) => t.id === tid);
        if (!current) return prev;
        return [{ ...current, updated_at: now }, ...rest];
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al consultar");
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Hubo un error al consultar. Intenta de nuevo." },
      ]);
    } finally {
      setSending(false);
    }
  };

  const enterSection = (label: string) => {
    setActiveSection(label);
    // Start a fresh thread for this section
    setThreadId(null);
    threadIdRef.current = null;
    setMessages([]);
    setInput("");
    // Send the section entry message
    // activeSection won't be set yet in `send`'s closure, so pass it directly
    void (async () => {
      const text = `Quiero saber sobre: ${label}`;
      const next: ChatMsg[] = [{ role: "user", content: text }];
      setMessages(next);
      setSending(true);
      try {
        const tid = await ensureThread(text);
        await supabase.from("knowledge_assistant_messages").insert({
          thread_id: tid,
          role: "user",
          content: text,
        });
        const { data, error } = await supabase.functions.invoke("assistant-chat", {
          body: {
            assistant_id: campaignId,
            organization_id: activeCampaign?.organization_id ?? activeWorkspaceId,
            messages: next.map((m) => ({ role: m.role, content: m.content })),
            section_label: label,
            section_entry: true,
          },
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        const reply: ChatMsg = {
          role: "assistant",
          content: data.reply ?? "",
          sources: data.sources ?? [],
          actions: data.actions ?? [],
        };
        setMessages((prev) => [...prev, reply]);
        await supabase.from("knowledge_assistant_messages").insert({
          thread_id: tid,
          role: "assistant",
          content: reply.content,
          sources: reply.sources ?? [],
          actions: reply.actions ?? [],
        });
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Error al consultar");
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: "Hubo un error al abrir la sección. Intenta de nuevo." },
        ]);
      } finally {
        setSending(false);
      }
    })();
  };

  const exitSection = useCallback(() => {
    setActiveSection(null);
  }, []);

  const openStudySection = (sourceId: string, label: string) =>
    send({ text: `Quiero estudiar: ${label}`, studySourceId: sourceId });

  const openStudyIndex = () => send({ text: "¿Por dónde empiezo?" });

  const isProfileLocked = !!forceProfile;

  // When locked to a profile, only show threads for that scope.
  const visibleThreads = forceProfile
    ? threads.filter((t) => t.scope === forceProfile)
    : threads;

  return {
    profile,
    setProfile,
    isProfileLocked,
    campaignId,
    setCampaignId,
    threads: visibleThreads,
    threadId,
    messages,
    input,
    setInput,
    loading,
    loadingThread,
    sending,
    bottomRef,
    campaignAssistants,
    platformAssistant,
    activeCampaign,
    studySections,
    activeSection,
    enterSection,
    exitSection,
    send,
    openStudySection,
    openStudyIndex,
    newChat,
    openThread,
    deleteThread,
    refreshThreads: loadThreads,
  };
}
